import { useClientColours } from "../lib/useClientColours";

/** Small coloured dot identifying a client at a glance (same colour on jobs, requests, calendar, Properties).
 *  Pass onClick to make it a "show only this client" filter button. */
export default function ClientDot({ clientId, name, size = 8, className = "", onClick, active = false }) {
  const { colourFor } = useClientColours();
  const colour = colourFor(clientId);
  if (!colour) return null;
  const style = { width: size, height: size, background: colour };
  if (onClick) {
    return (
      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(clientId); }}
        className={`inline-flex items-center justify-center rounded-full shrink-0 p-1 -m-1 hover:scale-125 focus-visible:scale-125 transition-transform ${className}`}
        title={active ? `Showing only ${name || "this client"} — click to clear` : `Show only ${name || "this client"}`}
        aria-label={active ? `Clear client filter ${name || ""}` : `Show only ${name || "this client"}`} aria-pressed={active}
        data-testid={`client-dot-${clientId}`}>
        <span className={`block rounded-full ring-1 ring-white/80 dark:ring-black/40 shadow-sm ${active ? "outline outline-2 outline-offset-1" : ""}`} style={{ ...style, outlineColor: active ? colour : undefined }} />
      </button>
    );
  }
  return (
    <span className={`inline-block rounded-full shrink-0 ring-1 ring-white/80 dark:ring-black/40 shadow-sm ${className}`}
      style={style} title={name ? `Client: ${name}` : "Client colour"}
      aria-label={name ? `Client ${name}` : "Client"} data-testid={`client-dot-${clientId}`} />
  );
}

/** "● Client name ×" chip shown while a list is filtered to one client. */
export function ClientFilterChip({ clientId, name, onClear, testid = "client-filter-chip" }) {
  const { colourFor } = useClientColours();
  const colour = colourFor(clientId);
  return (
    <span className="inline-flex items-center gap-1.5 h-8 pl-2.5 pr-1 rounded-full border text-xs font-medium bg-background"
      style={{ borderColor: colour || undefined }} data-testid={testid}>
      {colour && <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: colour }} />}
      <span className="truncate max-w-[180px]">{name || "One client"}</span>
      <button type="button" onClick={onClear} className="h-5 w-5 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground" title="Show all clients" aria-label="Clear client filter" data-testid={`${testid}-clear`}>×</button>
    </span>
  );
}
